import React, { useEffect, useState, useMemo, useRef, useContext } from 'react'
import { Container, MultiSelector } from '../../../components'
import {
    Folder,
    IdBoolMap,
    Inspection,
    Unit,
    UnitInspectFreqConfig,
    User,
    getLineageUnits,
} from '../../../models'
import { AreaSelectorCollection } from '../../../components/AreaSelector'
import {
    useAppDispatch,
    useAptConfig,
    useAreaStatusTag,
    _useFinderSelection,
    useRootInfrastructure,
} from '../../../hooks'
import { SchedulerFinder } from '../../Scheduler_v2/SchedulerFinder'
import {
    ASSIGN_UNIT_INSPECTION_REQUEST,
    FinderLocationSelection,
    FinderSelectionMode,
    GET_INSPECTION_LIST,
    RootState,
    assignUnitInspections,
    getInspectionList,
    setInspectionLoading,
} from '../../../store'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Fade,
    Paper,
    FormControlLabel,
    Switch,
    Theme,
    Typography,
    Box,
} from '@material-ui/core'
import { InspectionAreaCard } from '../InspectionAreaCard'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { AssignmentProgressContext } from './CreateInspectionModal'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    theme: Theme
    inspection: Inspection
    userList: User[]
    selectedUnits: Unit[]
    setSelectedUnits: (units: Unit[]) => void
    completeStep: () => void
    offlineInspection: boolean
    setOfflineInspection: (offlineInspection: boolean) => void
    moveOutInspections: boolean
    setMoveOutInspections: (moveOutInspections: boolean) => void
}

export const AssignInspectionStep = (props: Props) => {
    const {
        theme,
        inspection,
        userList,
        selectedUnits,
        setSelectedUnits,
        completeStep,
        offlineInspection,
        setOfflineInspection,
        moveOutInspections,
        setMoveOutInspections,
    } = props

    const dispatch = useAppDispatch()
    const [assignedTo, setAssignedTo] = useState<number[]>([])
    const [areaSelection, setAreaSelection] = useState<IdBoolMap>({})
    const [openWarningDialog, setOpenWarningDialog] = useState(false)
    const { getAreaConfigMap } = useAptConfig({ areaConfigList: true })
    const { tree } = useRootInfrastructure(true)
    const areaConfigMap = getAreaConfigMap()
    const finderSelection = _useFinderSelection({
        whichSelection: FinderLocationSelection.RootSelection,
        selectionMode: FinderSelectionMode.Recursive,
        useCase: 'inspection',
    })
    const { progress } = useContext(AssignmentProgressContext)
    const isProgressComplete =
        progress.status === 'completed' || progress.status === 'error'

    const newSelectedUnits = useMemo(() => {
        const units: Unit[] = []
        const visitFolder = (folder: Folder) => {
            folder.children.forEach(visitFolder)
            folder.units.forEach((treeUnit) => {
                const unit = finderSelection.selection.unit[treeUnit.id]
                if (unit !== undefined) {
                    units.push(unit)
                }
            })
        }
        visitFolder(tree.root)
        return units
    }, [finderSelection.selection, tree.root])
    const prevSelectedUnitsRef = useRef(newSelectedUnits)

    useEffect(() => {
        if (offlineInspection && isProgressComplete) {
            completeStep()
        }
    }, [isProgressComplete])

    useEffect(() => {
        if (prevSelectedUnitsRef.current.length !== newSelectedUnits.length) {
            setSelectedUnits(newSelectedUnits)
            prevSelectedUnitsRef.current = newSelectedUnits
        }
    }, [newSelectedUnits])

    const { areaStatusTagList, areaStatusTagMap } = useAreaStatusTag({
        getListForSchedule: inspection.schedule?.id,
    })

    const handleAssign = () => {
        const areaList = Object.keys(areaSelection).reduce<number[]>(
            (prev, areaIdStr) => {
                const areaId = Number(areaIdStr)
                if (areaSelection[areaId]) {
                    return prev.concat(areaId)
                }
                return prev
            },
            [],
        )

        // Dispatch the action and wait for completion before moving to next step
        dispatch(
            assignUnitInspections({
                body: {
                    inspection: inspection.id,
                    areas: areaList,
                    assign_to: assignedTo.length > 0 ? assignedTo : undefined,
                    move_out_inspection: moveOutInspections,
                },
            }),
        ).catch(() => {
            toast.error('Failed to assign units to inspection')
        })
    }

    const buildAreaStateForUnit = (
        unit: Unit,
        selectedAreaState: IdBoolMap,
        mutableAreaState: IdBoolMap,
    ) => {
        unit.areas.forEach((area) => {
            if (selectedAreaState[area.id] !== undefined) {
                mutableAreaState[area.id] = selectedAreaState[area.id]
            } else if (
                !areaStatusTagMap ||
                areaStatusTagMap[area.id] === undefined
            ) {
                mutableAreaState[area.id] = true
            } else {
                mutableAreaState[area.id] = areaStatusTagMap[
                    area.id
                ]!.area_status_config.should_service
            }
        })
    }

    useEffect(() => {
        const mutableAreaState: IdBoolMap = {}
        const handledUnits: IdBoolMap = {}

        // Recursively get the units of the selected folders
        Object.keys(finderSelection.selection.folder).forEach((key) => {
            if (key === 'length') return

            const folder = finderSelection.selection.folder[parseInt(key)]
            if (folder === undefined) return

            const folderUnits = getLineageUnits(folder)

            // It is possible that folderUnits are already in handledUnits because
            // The user selected both ParentFolder and ChildFolder
            // Prevent units from being duplicated

            folderUnits.forEach((unit) => {
                if (handledUnits[unit.id]) return
                handledUnits[unit.id] = true
                buildAreaStateForUnit(unit, areaSelection, mutableAreaState)
            })
        })

        // Add in the individual units the user selected
        Object.keys(finderSelection.selection.unit).forEach((key) => {
            if (key === 'length') return
            const unit = finderSelection.selection.unit[parseInt(key)]
            if (unit === undefined) return
            if (handledUnits[unit.id]) return
            handledUnits[unit.id] = true
            buildAreaStateForUnit(unit, areaSelection, mutableAreaState)
        })

        setAreaSelection(mutableAreaState)
    }, [finderSelection.selection])

    return (
        <Container
            style={{
                flexDirection: 'column',
                overflowY: 'hidden',
                flex: 1,
            }}
        >
            {/* Header */}
            <Container
                style={{
                    padding: theme.spacing(1),
                    paddingLeft: theme.spacing(2),
                    paddingRight: theme.spacing(2),
                }}
            >
                <AreaSelectorCollection
                    areaConfigMap={areaConfigMap}
                    areaSelectionMap={areaSelection}
                    setAreaSelectionMap={setAreaSelection}
                    units={selectedUnits}
                />

                <div style={{ flex: 1 }} />

                {/* Move Out Inspections */}
                <FormControlLabel
                    control={
                        <Switch
                            checked={moveOutInspections}
                            onChange={(e) =>
                                setMoveOutInspections(e.target.checked)
                            }
                            color="primary"
                        />
                    }
                    label="Move Out Inspections"
                    style={{ marginRight: theme.spacing(2) }}
                />

                {/* Offline inspection */}
                <FormControlLabel
                    control={
                        <Switch
                            checked={offlineInspection}
                            onChange={(e) =>
                                setOfflineInspection(e.target.checked)
                            }
                            color="primary"
                        />
                    }
                    label="Offline Inspection"
                    style={{ marginRight: theme.spacing(2) }}
                />

                <MultiSelector
                    label="Assigned To"
                    keyIdentifier="ASSIGN_INSPECTION_STEP"
                    options={userList}
                    selectedOptions={assignedTo}
                    onChange={(newOptions) => setAssignedTo(newOptions)}
                    style={{
                        formControl: {
                            width: 200,
                            marginRight: theme.spacing(2),
                        },
                    }}
                />
            </Container>
            {/* Body */}
            <Container
                style={{
                    overflowY: 'hidden',
                    flex: 1,
                }}
            >
                {/* Left Column, Tree selection */}
                <Container
                    style={{
                        overflowY: 'scroll',
                        width: 300,
                    }}
                >
                    <SchedulerFinder
                        root={tree.root}
                        finderSelection={finderSelection}
                        areaConfigMap={areaConfigMap}
                        areaStatusTagMap={{}}
                        unitWorkorderMap={{}}
                        useCase="inspection"
                    />
                </Container>
                {/* Right Column Unit selection */}
                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                        overflowY: 'scroll',
                    }}
                >
                    {selectedUnits.map((unit) => {
                        return (
                            <Container
                                key={`UNIT-${unit.id}`}
                                style={{
                                    minHeight: 100,
                                    maxHeight: 100,
                                    border: `1px solid ${theme.palette.grey[400]}`,
                                    borderRadius: theme.shape.borderRadius,
                                    margin: theme.spacing(1),
                                }}
                            >
                                {/* Left Column Location Details */}
                                <Container
                                    style={{
                                        flexDirection: 'column',
                                        borderRight: `1px solid ${theme.palette.grey[400]}`,
                                        padding: theme.spacing(1),
                                        minWidth: 150,
                                        maxWidth: 150,
                                        justifyContent: 'center',
                                    }}
                                >
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.subtitle2,
                                            ),
                                            color: theme.palette.grey[500],
                                        }}
                                    >
                                        {unit.folder.path}
                                        {unit.folder.name}
                                    </span>
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.subtitle1,
                                            ),
                                            fontWeight:
                                                theme.typography.fontWeightBold,
                                            color: theme.palette.grey[700],
                                        }}
                                    >
                                        {unit.name}
                                    </span>
                                </Container>

                                {/* Right Column Area Selections */}
                                <Container
                                    style={{
                                        flex: 1,
                                        padding: theme.spacing(1),
                                        overflowX: 'scroll',
                                    }}
                                >
                                    {unit.areas.map((area) => {
                                        return (
                                            <InspectionAreaCard
                                                key={`ASSIGN_INSPECT_AREA_${area.id}`}
                                                area={area}
                                                areaConfig={
                                                    areaConfigMap[
                                                        area.area_config
                                                    ]
                                                }
                                                areaStatusTag={
                                                    areaStatusTagMap[area.id]
                                                }
                                                setSelectionState={() =>
                                                    setAreaSelection({
                                                        ...areaSelection,
                                                        [area.id]: !areaSelection[
                                                            area.id
                                                        ],
                                                    })
                                                }
                                                selectionState={areaSelection}
                                            />
                                        )
                                    })}
                                </Container>
                            </Container>
                        )
                    })}
                </Container>
            </Container>
            {/* Footer */}
            <Container
                style={{
                    justifyContent: 'flex-end',
                    padding: theme.spacing(2),
                }}
            >
                {progress.status === 'pending' ? (
                    <Button
                        onClick={() => {
                            if (assignedTo.length > 0) {
                                setOpenWarningDialog(true)
                            } else {
                                handleAssign()
                            }
                        }}
                        variant="outlined"
                        color="primary"
                        style={{ textTransform: 'none' }}
                    >
                        Create Assignments
                    </Button>
                ) : (
                    <Fade in={true}>
                        <Paper
                            elevation={3}
                            style={{
                                padding: theme.spacing(2),
                                marginBottom: theme.spacing(2),
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <Typography
                                variant={'subtitle1'}
                                gutterBottom
                                style={{
                                    fontWeight: 'normal',
                                    color: 'inherit',
                                }}
                            >
                                {progress.status === 'in_progress'
                                    ? `Assigning units to inspection... (${Math.round(
                                          progress.progress,
                                      )}%)`
                                    : progress.status === 'completed'
                                    ? 'Units successfully assigned to inspection!'
                                    : progress.status === 'error'
                                    ? 'Error assigning units to inspection'
                                    : 'Waiting to start unit assignment...'}
                            </Typography>
                            <Box
                                position="relative"
                                display="flex"
                                alignItems="center"
                                width="100%"
                                height={10}
                                bgcolor="grey.200"
                                borderRadius={5}
                                mb={1}
                            >
                                <Box
                                    className="progress-indicator"
                                    position="absolute"
                                    left={0}
                                    top={0}
                                    height="100%"
                                    bgcolor={
                                        progress.status === 'error'
                                            ? theme.palette.error.main
                                            : progress.status === 'completed'
                                            ? theme.palette.success.main
                                            : theme.palette.primary.main
                                    }
                                    borderRadius={5}
                                    style={{
                                        width: `${progress.progress}%`,
                                        transition:
                                            'width 0.3s ease-in-out, background-color 0.3s ease',
                                    }}
                                />
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                {progress.message ||
                                    (progress.progress > 0
                                        ? `${Math.round(
                                              progress.progress,
                                          )}% complete`
                                        : 'Starting...')}
                            </Typography>
                        </Paper>
                    </Fade>
                )}
            </Container>
            <Dialog
                open={openWarningDialog}
                onClose={() => {
                    setOpenWarningDialog(false)
                }}
            >
                <DialogTitle id="alert-dialog-title">
                    Assign user to inspection?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to assign this inspection to
                        specific users? Only the selected users will be able to
                        see the inspection on the app.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="secondary"
                        variant="outlined"
                        onClick={() => {
                            setOpenWarningDialog(false)
                        }}
                    >
                        Cancel
                    </Button>
                    <div style={{ flex: 1 }} />
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={() => {
                            // First close the dialog
                            setOpenWarningDialog(false)
                            // Then handle the assignment
                            handleAssign()
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
